// Copyright (c) StrangeBee 2024

import { ICONS } from '@Constants/iconsApp';
import { FormatDate } from '@Molecules/Format';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { AnalysisQuickview } from '@Organisms/AnalysisData/AnalysisQuickview';
import { ObservableEventList } from '@Organisms/Observable/eventList';
import { useGenericQuery } from '@Queries/genericQueries';
import React, { ReactElement } from 'react';
import { ObservableOutput, ActionOutput } from 'thehive-sdk';

interface AnalyzerReportProps {
    analyse: ActionOutput;
    eventList: ObservableEventList;
    extend?: boolean;
    observable?: ObservableOutput;
    importObservable?: boolean;
    indexFirst?: boolean;
    defaultVisible?: boolean;
}

const labels = {
    Success: { icon: <ICONS.CheckIcon /> },
    Failure: { icon: <ICONS.WarningOutlined /> },
    Waiting: { icon: <ICONS.SyncOutlined spin /> },
};

export const AnalyzerReport = ({
    extend,
    analyse,
    observable,
    eventList,
    importObservable = true,
    indexFirst,
    defaultVisible = false,
}: AnalyzerReportProps): ReactElement => {
    const { status = 'Waiting', _createdAt, endDate, _id: jobId } = analyse || {};

    const { data: analysisData, refetch } = useGenericQuery({
        manual: true,
        fetchOnStart: false,
        fetchCallback: () => eventList.onGetReport(jobId),
        key: ['get-observable-report', jobId],
    });

    const isWaiting = status === 'Waiting';

    const onDrawerOpen = (): void => {
        refetch();
    };

    return (
        <AnalysisQuickview
            key={jobId}
            observable={observable}
            data={analysisData}
            importObservable={importObservable}
            eventList={eventList}
            onDrawerOpen={onDrawerOpen}
            defaultVisible={defaultVisible}
            triggerElem={
                extend || indexFirst ? (
                    <THButton
                        key={jobId}
                        type="link"
                        className={'analyzer-analysis'}
                        disabled={isWaiting}
                        danger={status === 'Failure'}
                        icon={labels[status]?.icon}
                    >
                        <FormatDate date={isWaiting ? _createdAt : endDate} />
                    </THButton>
                ) : undefined
            }
        />
    );
};
